import { getAuthToken, invalidateSession } from '../utils/authSession';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8082/api';
export const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || 'http://localhost:8082/images';
const PATIENT_IMAGE_VERSION = 'sora-20260820';

export const getPatientImageUrl = (caseId) => `${IMAGE_BASE_URL}/${encodeURIComponent(caseId)}.png?v=${PATIENT_IMAGE_VERSION}`;

async function request(path, { method = 'GET', body, signal, authenticated = true } = {}) {
  const token = authenticated ? getAuthToken() : null;
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method, headers, signal,
    ...(body === undefined ? {} : { body: JSON.stringify(body) })
  });
  if (response.status === 401 && authenticated) {
    invalidateSession(token);
    const error = new Error('Authentication expired');
    error.status = 401;
    throw error;
  }
  const result = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(result?.message || result?.error || `Request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  if (result == null) throw new Error('The server returned an invalid response');
  return result;
}

const dataRequest = async (path, options) => {
  const result = await request(path, options);
  return result.data ?? result;
};
const casePath = (id) => `/doctor-review/cases/${encodeURIComponent(id)}`;
const submissionPath = (id) => `/doctor-review/submissions/${encodeURIComponent(id)}`;

export const apiService = {
  register: (body) => request('/auth/register', { method: 'POST', body, authenticated: false }),
  login: (body) => request('/auth/login', { method: 'POST', body, authenticated: false }),
  getCurrentUser: (options = {}) => dataRequest('/auth/me', options),
  getCases: (language = 'en', options = {}) => request(`/cases?language=${encodeURIComponent(language)}`, options),
  sendChatMessage(caseId, message, phase = 'Anamnesis', transcript = [], language = 'en', options = {}) {
    return request('/chat', {
      method: 'POST', signal: options.signal,
      body: { case_id: caseId, session_id: options.sessionId, message, phase, language }
    });
  },
  evaluateDiagnosis(caseId, diagnosis, managementPlan, transcript = [], language = 'en', options = {}) {
    return request('/evaluate', {
      method: 'POST', signal: options.signal,
      body: { case_id: caseId, session_id: options.sessionId, diagnosis, management_plan: managementPlan, language }
    });
  },
  translateTexts: (texts = [], language = 'en', options = {}) => request('/translate-texts', {
    method: 'POST', signal: options.signal, body: { texts, language, strict: Boolean(options.strict) }
  }),
  getMySubmissions: (options = {}) => dataRequest('/submissions/mine', options),
  getDoctorCases: () => dataRequest('/doctor-review/cases'),
  getCaseLabs: (caseId, language = 'en', options = {}) => dataRequest(`/cases/${encodeURIComponent(caseId)}/labs?language=${encodeURIComponent(language)}${options.sessionId ? `&session_id=${encodeURIComponent(options.sessionId)}` : ''}`),
  createCase: (body) => dataRequest('/doctor-review/cases', { method: 'POST', body }),
  updateCase: (caseId, body) => dataRequest(casePath(caseId), { method: 'PUT', body }),
  getCaseDetail: (caseId) => dataRequest(casePath(caseId)),
  getSubmission: (submissionId) => dataRequest(submissionPath(submissionId)),
  getSubmissions: () => dataRequest('/doctor-review/submissions'),
  submitDoctorScore: (submissionId, historyScore, diagnosisScore, managementScore, comment = '') => dataRequest(`${submissionPath(submissionId)}/score`, {
    method: 'POST', body: {
      doctor_score_history: historyScore, doctor_score_diagnosis: diagnosisScore,
      doctor_score_management: managementScore, doctor_comment: comment
    }
  }),
  getPatientImage: (caseId) => request(`/patient-image/${encodeURIComponent(caseId)}`),
  startSession: (caseId, options = {}) => request('/session/start', { method: 'POST', body: { caseId, language: options.language || 'en' }, signal: options.signal })
};
